﻿import type { NuxtError } from '#app';

import { FetchError, type FetchResponse } from 'ofetch';

const API_FETCH_ERROR_SIGNATURE = '__api_fetch_nuxt_error' as const;

type CustomResponseError = { error: string; error_description?: string };

export function createApiFetchError(error: unknown) {
    if (isApiFetchError(error)) return error;

    // Try to extract FetchError from passed error
    let fetchError = isFetchError(error) ? error : isNuxtError(error) && isFetchError(error.cause) ? error.cause : undefined;
    if (!fetchError)
        return error;

    if (fetchError.response)
        return toApiFetchError({
            status: fetchError.response.status,
            statusText: 'Fetch Error',
            data: collectErrorsFromResponse(fetchError.response),
            fatal: fetchError.response.status !== 400,
        });

    // request failed
    return toApiFetchError({
        status: 503,
        statusText: 'Fetch Error',
        data: (fetchError.cause as Error)?.message,
        fatal: false,
    });
}

function collectErrorsFromResponse(response: FetchResponse<any>) {
    const data = response._data;

    if (responseContainsCustomErrors(data)) {
        const errors = Array.isArray(data) ? data : [data];

        return errors.map((e) => e.error_description || e.error);
    }

    const error = response.headers.get('error')
    if (error) return error;

    if (typeof data === 'string') return data;

    return undefined;
}

function responseContainsCustomErrors(data?: any): data is CustomResponseError | CustomResponseError[] {
    if (!data) return false;

    if (Array.isArray(data)) {
        // If array is array of custom errors
        return data.every((e) => typeof e.error === 'string');
    }

    // If single error
    return typeof data.error === 'string';
}

// https://github.com/nuxt/nuxt/blob/v4.4.8/packages/nuxt/src/app/composables/error.ts
function toApiFetchError<DataT = any>(error: Partial<NuxtError<DataT>>) {
    const nuxtError = createError(error);

    Object.defineProperty(nuxtError, API_FETCH_ERROR_SIGNATURE, { value: true, configurable: false, writable: false });

    return nuxtError;
}

// https://github.com/unjs/ofetch/blob/main/src/error.ts
function isFetchError(error: unknown): error is FetchError {
    return !!error && typeof error === 'object' && 'name' in error && error.name === 'FetchError' && 'request' in error;
}

function isApiFetchError(error: unknown) {
    return isNuxtError(error) && API_FETCH_ERROR_SIGNATURE in error;
}